import React, { useState, useEffect, useRef } from "react";
import { pipeline } from "@xenova/transformers";

export default function Chatbot({ tools }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [embedder, setEmbedder] = useState(null);
  const [toolEmbeddings, setToolEmbeddings] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    async function loadModel() {
      const model = await pipeline("feature-extraction", "Xenova/all-MiniLM-L6-v2");
      setEmbedder(model);

      const embeddings = await Promise.all(
        tools.map(async (t) => {
          const emb = await model(t.name + " " + t.description);
          return { id: t.id, vector: emb.data[0], tool: t };
        })
      );
      setToolEmbeddings(embeddings);
    }
    loadModel();
  }, [tools]);

  const cosineSim = (a, b) => {
    const dot = a.reduce((sum, ai, i) => sum + ai * b[i], 0);
    const normA = Math.sqrt(a.reduce((s, ai) => s + ai * ai, 0));
    const normB = Math.sqrt(b.reduce((s, bi) => s + bi * bi, 0));
    return dot / (normA * normB);
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    const newMsg = { role: "user", text: input };
    setMessages((m) => [...m, newMsg]);
    setInput("");

    if (!embedder) {
      setMessages((m) => [...m, { role: "bot", text: "Model loading..." }]);
      return;
    }

    const queryEmb = await embedder(input);
    const queryVec = queryEmb.data[0];

    const ranked = toolEmbeddings
      .map((te) => ({ ...te, score: cosineSim(queryVec, te.vector) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);

    const answer =
      ranked.length > 0
        ? `Here are some tools related to your question:\n\n${ranked
            .map(
              (r) =>
                `🧠 <b>${r.tool.name}</b> – ${r.tool.description}<br/>🔗 <a href="${r.tool.url}" target="_blank">Visit</a>`
            )
            .join("<br/><br/>")}`
        : "I couldn’t find any matching tools.";

    setMessages((m) => [...m, { role: "bot", text: answer }]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="chatbot-popup">
      <div className="chatbot-header">🤖 BucketGPT Assistant</div>
      <div className="messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role}>
            <p dangerouslySetInnerHTML={{ __html: m.text.replace(/\n/g, "<br>") }} />
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="input-bar">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about any AI tool..."
        />
        <button onClick={handleSend}>➤</button>
      </div>
    </div>
  );
}
